import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import DeliveryOptions from "../DeliveryOptions/DeliveryOptions";
import { ProductDeliveryService } from "../../../../services/ProductDeliveryService";

interface DeliveryOptionsContainerProps {
  displayedProduct: any;
  availableDeliveryMethods: any;
  opsLeadTime: number | null;
  setDeliveryMessage: (message: string | null) => void;
  setIsAddToCartDisabled: (disabled: boolean) => void;
}

const DeliveryOptionsContainer: React.FC<DeliveryOptionsContainerProps> = ({
  displayedProduct,
  availableDeliveryMethods,
  opsLeadTime,
  setDeliveryMessage,
  setIsAddToCartDisabled,
}) => {
  const [expressAvailable, setExpressAvailable] = useState(false);
  const [expressCountdown, setExpressCountdown] = useState<string | null>(null);
  const [expressNextAvailable, setExpressNextAvailable] = useState<
    string | null
  >(null);
  const [rapidoDateRange, setRapidoDateRange] = useState<string | null>(null);
  const [standardStartDate, setStandardStartDate] = useState<string | null>(
    null
  );
  const [rapidoCountdown, setRapidoCountdown] = useState<string | null>(null);
  const [standardCountdown, setStandardCountdown] = useState<string | null>(
    null
  );

  // Get destination zone IDs from Redux
  const destinationLeadTimeZoneId = useSelector(
    (state: any) => state.location.deliveryLeadTimeZone?.documentId
  );
  const destinationFeeZoneId = useSelector(
    (state: any) => state.location.deliveryFeeZone?.documentId
  );

  useEffect(() => {
    if (
      displayedProduct &&
      displayedProduct.partner &&
      displayedProduct.partner.daysOpen &&
      Object.keys(displayedProduct.partner.daysOpen).length > 0 &&
      destinationLeadTimeZoneId &&
      destinationFeeZoneId
    ) {
      // Use the new ProductDeliveryService for delivery calculations
      const calculateDeliveryOptions = async () => {
        try {
          const deliveryResult =
            await ProductDeliveryService.calculateProductDeliveryOptions(
              displayedProduct,
              destinationLeadTimeZoneId,
              destinationFeeZoneId
            );

          // Apply route availability validation for express delivery
          const productAllowsExpress =
            displayedProduct?.logistics?.expressDelivery === true;
          const routeSupportsExpress =
            availableDeliveryMethods?.express === true;
          const finalExpressAvailable =
            deliveryResult.express.available &&
            productAllowsExpress &&
            routeSupportsExpress;

          // Update state with results
          setExpressAvailable(finalExpressAvailable);
          setExpressCountdown(deliveryResult.express.countdown || null);

          // Set next available message when Express is not available
          if (
            !finalExpressAvailable &&
            deliveryResult.express.nextAvailableMessage
          ) {
            setExpressNextAvailable(
              deliveryResult.express.nextAvailableMessage
            );
          } else {
            setExpressNextAvailable(null);
          }

          // Apply route availability for rapido
          const rapidoAvailable =
            deliveryResult.rapido.available && availableDeliveryMethods?.rapido;
          setRapidoDateRange(
            rapidoAvailable ? deliveryResult.rapido.displayText : null
          );
          setRapidoCountdown(deliveryResult.rapido.countdown || null);

          // Apply route availability for standard
          const standardAvailable =
            deliveryResult.standard.available &&
            availableDeliveryMethods?.standard;
          setStandardStartDate(
            standardAvailable
              ? deliveryResult.standard.displayText
              : "No disponible"
          );
          setStandardCountdown(deliveryResult.standard.countdown || null);

          setDeliveryMessage(null);
          setIsAddToCartDisabled(false);
        } catch (error) {
          console.error(
            "❌ [DELIVERY] Error calculating delivery dates:",
            error
          );
          setDeliveryMessage("Error al calcular fechas de entrega");
          setIsAddToCartDisabled(true);
          // Reset all delivery options on error
          setExpressAvailable(false);
          setExpressCountdown(null);
          setExpressNextAvailable(null);
          setRapidoDateRange(null);
          setRapidoCountdown(null);
          setStandardStartDate("No disponible");
          setStandardCountdown(null);
        }
      };

      calculateDeliveryOptions();
    } else {
      // Reset delivery options when requirements aren't met
      setExpressAvailable(false);
      setExpressCountdown(null);
      setExpressNextAvailable(null);
      setRapidoDateRange(null);
      setRapidoCountdown(null);
      setStandardStartDate("No disponible");
      setStandardCountdown(null);
    }
  }, [
    displayedProduct,
    availableDeliveryMethods,
    opsLeadTime,
    destinationLeadTimeZoneId,
    destinationFeeZoneId,
    setDeliveryMessage,
    setIsAddToCartDisabled,
  ]);

  return (
    <DeliveryOptions
      expressAvailable={expressAvailable}
      expressCountdown={expressCountdown}
      expressNextAvailable={expressNextAvailable}
      rapidoDateRange={rapidoDateRange}
      standardStartDate={standardStartDate}
      rapidoCountdown={rapidoCountdown}
      standardCountdown={standardCountdown}
    />
  );
};

export default DeliveryOptionsContainer;
